import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL_WITH_API } from '../config/api';
import AdminHeader from '../components/AdminHeader';
import ImpersonationBanner from '../components/ImpersonationBanner';
import SideMenuCard, { SideMenuItem } from '../components/SideMenuCard';
import { isStagingOrLocalhost } from '../utils/environment';
import { clearAllAuthStorage, getEffectiveToken, getEffectiveUser } from '../utils/authSession';
import { getChromeRuntime } from '../utils/extensionMessaging';
import { getExtensionId } from '../config/extension';
import { MENU_ICON_PATHS } from '../components/menuIcons';

interface FeedbackRow {
  id: string;
  timestamp: string;
  email: string;
  name: string;
  role: string;
  domain: string;
  rating: number | null;
  issueExplanation: string;
  suggestedFeature: string;
  attachmentCount?: number;
}

type SortColumn = 'timestamp' | 'email' | 'name' | 'role' | 'rating' | 'issueExplanation' | 'suggestedFeature' | 'attachment';
type SortDirection = 'asc' | 'desc';

const AdminFeedbackPage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const usePersistentSidebar = isStagingOrLocalhost();

  useEffect(() => {
    document.title = 'Feedback - Traffic Lite Super Admin';
    const viewer = getEffectiveUser();
    if (!viewer?.isSuperAdmin) {
      navigate('/admin');
      return;
    }
    void fetchFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL_WITH_API}/feedback/admin`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch feedback');
      }
      setRows(Array.isArray(data.submissions) ? data.submissions : []);
      setAverageRating(Number(data.averageRating || 0));
    } catch (fetchError: unknown) {
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const renderSortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return null;
    return (
      <span className="traffic-sort-indicator" style={{ marginLeft: '6px', display: 'inline-flex', alignItems: 'center' }}>
        <img
          src={sortDirection === 'asc' ? MENU_ICON_PATHS.upArrow : MENU_ICON_PATHS.downArrow}
          alt=""
          style={{ width: '11px', height: '11px', objectFit: 'contain' }}
        />
      </span>
    );
  };

  const domainOptions = useMemo(() => {
    const domains = Array.from(new Set(rows.map((item) => (item.domain || '').trim()).filter(Boolean)));
    return domains.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!selectedDomain) return rows;
    return rows.filter((item) => (item.domain || '').toLowerCase() === selectedDomain.toLowerCase());
  }, [rows, selectedDomain]);

  const sortedRows = useMemo(() => {
    const next = [...filteredRows];
    next.sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';
      if (sortColumn === 'timestamp') {
        aValue = new Date(a.timestamp).getTime();
        bValue = new Date(b.timestamp).getTime();
      } else if (sortColumn === 'attachment') {
        aValue = Number(a.attachmentCount || 0);
        bValue = Number(b.attachmentCount || 0);
      } else if (sortColumn === 'rating') {
        aValue = Number.isInteger(a.rating) && Number(a.rating) > 0 ? Number(a.rating) : -1;
        bValue = Number.isInteger(b.rating) && Number(b.rating) > 0 ? Number(b.rating) : -1;
      } else {
        aValue = (a[sortColumn] as string | number) || '';
        bValue = (b[sortColumn] as string | number) || '';
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const compare = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
      return sortDirection === 'asc' ? compare : -compare;
    });
    return next;
  }, [filteredRows, sortColumn, sortDirection]);

  const formatTimestamp = (isoDate: string) => {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
  };

  const handleDownloadAttachment = async (feedbackId: string, attachmentCount = 0) => {
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        return;
      }
      if (attachmentCount < 1) return;
      for (let index = 0; index < attachmentCount; index += 1) {
        let response = await fetch(`${API_BASE_URL_WITH_API}/feedback/admin/${feedbackId}/attachment/${index}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        // Fallback for deployments still serving the legacy non-indexed attachment route.
        if (!response.ok && index === 0) {
          response = await fetch(`${API_BASE_URL_WITH_API}/feedback/admin/${feedbackId}/attachment`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
        if (!response.ok) {
          throw new Error('Attachment download failed.');
        }
        const blob = await response.blob();
        const disposition = response.headers.get('content-disposition') || '';
        const fileNameMatch = disposition.match(/filename="([^"]+)"/);
        const fileName = fileNameMatch?.[1] || `feedback-attachment-${feedbackId}-${index + 1}`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (downloadError: unknown) {
      setError(downloadError instanceof Error ? downloadError.message : 'Attachment download failed.');
    }
  };

  const escapeCsvValue = (value: string) => `"${String(value || '').replace(/"/g, '""')}"`;

  const handleExportCsv = () => {
    const headers = ['Timestamp', 'Email', 'Name (Last, First)', 'Role', 'Rating', 'Explanation of Issue', 'Suggested Feature'];
    const csvRows = sortedRows.map((item) => ([
      formatTimestamp(item.timestamp),
      item.email || '',
      item.name || '',
      item.role || '',
      String(item.rating || ''),
      item.issueExplanation || '',
      item.suggestedFeature || ''
    ]));
    const csv = [
      headers.map(escapeCsvValue).join(','),
      ...csvRows.map((row) => row.map((cell) => escapeCsvValue(cell)).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    link.href = url;
    link.download = `traffic-lite-feedback-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) return;
    clearAllAuthStorage();
    const runtime = getChromeRuntime();
    if (runtime) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) runtime.sendMessage(extensionId, message, () => {});
        else runtime.sendMessage(message, () => {});
      } catch (sendError) {
        console.error('Sign-out storage clear failed:', sendError);
      }
    }
    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  const sidebarItems: SideMenuItem[] = [
    { key: 'users', label: 'Users', onClick: () => navigate('/admin/users'), icon: MENU_ICON_PATHS.users },
    { key: 'domains', label: 'Domains', onClick: () => navigate('/admin/domains'), icon: MENU_ICON_PATHS.domains },
    { key: 'dashboard', label: 'Data Dashboard', onClick: () => navigate('/admin'), icon: MENU_ICON_PATHS.dataDashboard },
    { key: 'policy', label: 'Policy Templates', onClick: () => navigate('/admin/settings'), icon: MENU_ICON_PATHS.policyDetails },
    { key: 'aiAssist', label: 'GPS', onClick: () => navigate('/admin/ai-assist'), icon: MENU_ICON_PATHS.aiAssist },
    { key: 'feedback', label: 'Feedback', onClick: () => navigate('/admin/feedback'), isActive: true, icon: MENU_ICON_PATHS.feedback },
    { key: 'signout', label: 'Sign Out', onClick: handleSignOut, isDanger: true, icon: MENU_ICON_PATHS.signOut }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className={usePersistentSidebar ? 'max-w-[1700px] mx-auto flex gap-6 items-start' : 'max-w-[1500px] mx-auto'}>
        {usePersistentSidebar && <SideMenuCard title="Super Admin Menu" items={sidebarItems} />}
        <div className={`${usePersistentSidebar ? 'flex-1 min-w-0' : ''} bg-white rounded-lg shadow-md p-8`} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <AdminHeader
            title="Traffic Lite Super Admin"
            subtitle="Feedback"
            helperText="Review and sort user feedback submissions across all domains."
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
          />
          <ImpersonationBanner />

          {error && (
            <div style={{ padding: '12px 16px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c00', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#111827' }}>
              Average Rating: {averageRating.toFixed(2)} / 5
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                style={{
                  minWidth: '220px',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">All domains</option>
                {domainOptions.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleExportCsv}
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: '#455a64',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Export CSV
              </button>
            </div>
          </div>
          <p className="text-gray-600" style={{ marginBottom: '12px' }}>
            {rows.length} response{rows.length !== 1 ? 's' : ''} across all domains
          </p>

          {loading ? (
            <div className="text-center py-14 text-gray-500">Loading feedback...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <colgroup>
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '160px' }} />
                  <col style={{ width: '170px' }} />
                  <col style={{ width: '90px' }} />
                  <col style={{ width: '65px' }} />
                  <col style={{ width: '320px' }} />
                  <col style={{ width: '320px' }} />
                  <col style={{ width: '95px' }} />
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: '#212529', borderBottom: '2px solid #212529' }}>
                    {([
                      ['timestamp', 'Timestamp'],
                      ['email', 'Email'],
                      ['name', 'Name (Last, First)'],
                      ['role', 'Role'],
                      ['rating', 'Rating'],
                      ['issueExplanation', 'Explanation of Issue'],
                      ['suggestedFeature', 'Suggested Feature'],
                      ['attachment', 'Attachment']
                    ] as Array<[SortColumn, string]>).map(([column, label], index) => (
                      <th
                        key={column}
                        onClick={() => handleSort(column)}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: 600,
                          color: 'white',
                          borderRight: index < 7 ? '1px solid rgba(255,255,255,0.3)' : undefined,
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        {label}{renderSortIndicator(column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((item, index) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #eee', backgroundColor: index % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', whiteSpace: 'nowrap' }}>{formatTimestamp(item.timestamp)}</td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>{item.email || '-'}</td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>{item.name || '-'}</td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', textTransform: 'capitalize' }}>{item.role || '-'}</td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee' }}>
                        {Number.isInteger(item.rating) && Number(item.rating) > 0 ? item.rating : '-'}
                      </td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', whiteSpace: 'normal', lineHeight: 1.45 }}>{item.issueExplanation || '-'}</td>
                      <td style={{ padding: '12px', borderRight: '1px solid #eee', whiteSpace: 'normal', lineHeight: 1.45 }}>{item.suggestedFeature || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        {Number(item.attachmentCount || 0) > 0 ? (
                          <button
                            type="button"
                            onClick={() => void handleDownloadAttachment(item.id, Number(item.attachmentCount || 0))}
                            title="Download attachment"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#2563eb',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: 600,
                              textDecoration: 'underline'
                            }}
                          >
                            Download
                          </button>
                        ) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sortedRows.length === 0 && (
                <div className="text-center py-12 text-gray-500">No feedback submitted yet.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminFeedbackPage;
